import {
  COLOR_RAMPS, computeBreaks, sampleRamp, graduatedColorExpression, categorizedColorExpression,
  uniqueCategories, numericValues,
} from './classify';
import { MAP_FONT_STACK } from './map-styles';

/**
 * Pure style model for the properties panel (components/properties/PropertiesPanel.js):
 * defaults, class previews, the MapLibre paint / layout expression builders and the
 * `labels` block. No React, no DOM; everything here is unit tested (layer-style.test.js).
 *
 * A layer style is { mode, fillColor, strokeColor, strokeWidth, opacity, pointSize, symbol,
 *   attr, method, nClasses, ramp, reversed, colorExpr, labels }. `colorExpr` is computed from
 * the classification when a style is applied; `labels` is the label block (DEFAULT_LABELS).
 */

export const MODES = [
  { id: 'single', label: 'Single' },
  { id: 'categorized', label: 'Categorized' },
  { id: 'graduated', label: 'Graduated' },
  { id: 'heatmap', label: 'Heatmap' },
];
export const BREAK_METHODS = [
  { id: 'equal', label: 'Equal Interval' },
  { id: 'quantile', label: 'Quantile' },
  { id: 'jenks', label: 'Natural Breaks' },
];
export const POINT_SYMBOLS = [
  { id: 'circle', label: 'Circle' },
  { id: 'square', label: 'Square' },
  { id: 'triangle', label: 'Triangle' },
];

export const DEFAULT_STYLE = {
  mode: 'single',
  fillColor: '#00d4ff',
  strokeColor: '#00d4ff',
  strokeWidth: 2,
  opacity: 0.9,
  pointSize: 5,
  symbol: 'circle',
  attr: '',
  method: 'quantile',
  nClasses: 5,
  ramp: 'viridis',
  reversed: false,
};

export const DEFAULT_LABELS = {
  field: '',
  size: 11,
  color: '#e6edf3',
  haloColor: '#0d1117',
  haloWidth: 1,
  placement: 'point', // 'point' | 'line'
  minZoom: 0,
  allowOverlap: false, // false = collision avoidance on
  decimals: null,      // null = as stored
  suffix: '',
};

/** Ramp colours for `n` classes, optionally reversed. */
export function rampColors(ramp, n, reversed = false) {
  const colors = sampleRamp(ramp, n);
  return reversed ? [...colors].reverse() : colors;
}

/**
 * Class preview for a classified style, or null when there is nothing to classify.
 * `breaksOverride` carries breaks computed off-thread (worker pool) for big layers.
 */
export function buildClassPreview(style, features, breaksOverride = null) {
  if (!style || !style.attr) return null;
  if (style.mode === 'graduated') {
    const vals = numericValues(features, style.attr);
    if (!vals.length) return null;
    const breaks = breaksOverride || computeBreaks(vals, style.nClasses, style.method);
    if (!breaks.length) return null;
    // Dedupe can shrink the breaks below nClasses+1: size the colours to the real class count.
    return { type: 'graduated', breaks, colors: rampColors(style.ramp, Math.max(1, breaks.length - 1), style.reversed) };
  }
  if (style.mode === 'categorized') {
    const cats = uniqueCategories(features, style.attr);
    if (!cats.length) return null;
    return { type: 'categorized', categories: cats, colors: rampColors(style.ramp, cats.length, style.reversed) };
  }
  return null;
}

/** MapLibre colour expression for a class preview (null for single / heatmap). */
export function colorExpressionFor(style, preview) {
  if (preview?.type === 'graduated') return graduatedColorExpression(style.attr, preview.breaks, preview.colors);
  if (preview?.type === 'categorized') return categorizedColorExpression(style.attr, preview.categories, preview.colors);
  return null;
}

/** The style to store on Apply / live preview: draft plus its computed `colorExpr`. */
export function finalizeStyle(style, preview) {
  return { ...style, colorExpr: colorExpressionFor(style, preview) };
}

/**
 * Paint for one layer kind ('circle' | 'line' | 'fill') from a style override. Without a
 * style the layer's own defaults are returned unchanged.
 */
export function stylePaintFor(style, kind, defaults) {
  const s = style;
  if (!s) return defaults;
  const color = s.colorExpr || (kind === 'line' ? s.strokeColor : s.fillColor);
  // Numbers are coerced so a null/NaN in a stored style never reaches MapLibre (V.23).
  const radius = num(s.pointSize, 5);
  const opacity = num(s.opacity, 0.9);
  const strokeWidth = Math.min(num(s.strokeWidth, 2), 3);
  if (kind === 'circle') return {
    'circle-radius': radius,
    'circle-color': color,
    'circle-opacity': opacity,
    // A classified layer's outline follows the class colour (no stray cyan ring, V.31)
    'circle-stroke-color': s.colorExpr || s.strokeColor,
    'circle-stroke-width': strokeWidth,
  };
  if (kind === 'line') return { 'line-color': color, 'line-width': num(s.strokeWidth, 2), 'line-opacity': opacity };
  if (kind === 'fill') return { 'fill-color': color, 'fill-opacity': opacity * 0.35 };
  return defaults;
}

/** A finite number, else the fallback. */
function num(v, fallback) {
  const n = typeof v === 'number' ? v : (v === null || v === undefined || v === '' ? NaN : Number(v));
  return Number.isFinite(n) ? n : fallback;
}

/** How a point layer renders under a style: 'circle' (default), 'symbol' or 'heatmap'. */
export function pointKind(style) {
  if (!style) return 'circle';
  if (style.mode === 'heatmap') return 'heatmap';
  if (style.symbol === 'square' || style.symbol === 'triangle') return 'symbol';
  return 'circle';
}

export const symbolImageId = (shape) => `ugis-sym-${shape}`;
/** SDF symbol images are 32px square with the shape 16px wide (see point-symbols.js). */
export const SYMBOL_SHAPE_PX = 16;

/** Layout + paint of the symbol layer that replaces a circle layer for square / triangle points. */
export function symbolSpec(style) {
  const color = style.colorExpr || style.fillColor;
  return {
    layout: {
      'icon-image': symbolImageId(style.symbol),
      'icon-size': (style.pointSize * 2) / SYMBOL_SHAPE_PX,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-color': color,
      'icon-opacity': style.opacity,
      'icon-halo-color': style.strokeColor,
      'icon-halo-width': Math.min(style.strokeWidth, 3),
    },
  };
}

/** Paint of a heatmap layer: density ramp from the style's colour ramp. */
export function heatmapPaint(style) {
  const colors = rampColors(style.ramp, 5, style.reversed);
  return {
    'heatmap-radius': Math.max(4, style.pointSize * 4),
    'heatmap-opacity': style.opacity,
    'heatmap-intensity': 1.2,
    'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
      0, 'rgba(0,0,0,0)', 0.2, colors[0], 0.4, colors[1], 0.6, colors[2], 0.8, colors[3], 1, colors[4]],
  };
}

// ── Labels ───────────────────────────────────────────────────────────────────

/**
 * Normalised label block of a style, or null when the layer is not labelled. Styles
 * saved before the properties panel only carry `labelField`; that maps to the defaults.
 */
export function resolveLabels(style) {
  if (!style) return null;
  const l = style.labels;
  if (l && l.field) return { ...DEFAULT_LABELS, ...l };
  if (!l && style.labelField) return { ...DEFAULT_LABELS, field: style.labelField, size: 10 };
  return null;
}

/** Display formatting for label values: fixed decimals plus a units suffix (numbers only). */
export function formatNumber(value, { decimals = null, suffix = '' } = {}) {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : (typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN);
  if (!Number.isFinite(n)) return String(value);
  const d = Number.isInteger(decimals) && decimals >= 0 ? Math.min(decimals, 12) : null;
  return `${d === null ? String(n) : n.toFixed(d)}${suffix || ''}`;
}

/** MapLibre `text-field` expression for a label block (number formatting aware). */
export function labelTextField(labels) {
  const get = ['get', labels.field];
  const d = Number.isInteger(labels.decimals) && labels.decimals >= 0 ? Math.min(labels.decimals, 12) : null;
  const suffix = labels.suffix || '';
  if (d === null && !suffix) return ['to-string', get];
  const num = d === null ? ['to-string', get]
    : ['number-format', get, { 'min-fraction-digits': d, 'max-fraction-digits': d }];
  return ['case', ['==', ['typeof', get], 'number'], suffix ? ['concat', num, suffix] : num, ['to-string', get]];
}

/**
 * The one symbol sub-layer of a labelled layer: { layout, paint, minzoom, geomType }.
 * `geomType` is the geometry filter MapDataLayers combines with the chart filter
 * ('LineString' for line placement, null otherwise).
 */
export function labelLayerSpec(labels) {
  const line = labels.placement === 'line';
  return {
    layout: {
      'text-field': labelTextField(labels),
      'text-font': MAP_FONT_STACK,
      'text-size': num(labels.size, 10),
      'text-allow-overlap': !!labels.allowOverlap,
      ...(line ? { 'symbol-placement': 'line', 'text-offset': [0, -0.6] }
        : { 'text-offset': [0, 1.1], 'text-anchor': 'top' }),
    },
    paint: {
      'text-color': labels.color,
      'text-halo-color': labels.haloColor,
      'text-halo-width': num(labels.haloWidth, 1),
    },
    minzoom: Math.max(0, Math.min(24, Number(labels.minZoom) || 0)),
    geomType: line ? 'LineString' : null,
  };
}

/** Ramp keys for the picker, with their kind. */
export const RAMP_OPTIONS = Object.entries(COLOR_RAMPS).map(([key, r]) => ({ key, label: r.label, type: r.type }));
